import { acceptHMRUpdate, defineStore } from 'pinia'
import { useFetchForAuth } from '../composables/useFetchForAuth'

const expiryDate = new Date()
expiryDate.setFullYear(expiryDate.getFullYear() + 1)

// @ts-ignore
export const useCarbarAuAuthStore = defineStore('carbarAuAuth', {
	namespaced: true,
	state: () => ({
		user: null,
		redirect: null,
		userToken: null,
		otpEmail: null,
		otpRequestedAt: null,
		otpFlow: null,
		sessionError: null,
		isSigningOut: false,
		// The API's CSRF token for /secure/* requests; kept in memory only
		csrfToken: null,
	}),
	getters: {
		isLoggedIn: (state) => !!state.user?.id,
		isNovated: (state) => state.user && state.user.isNovated,
		isNovatedFirstTime: (state) =>
			state.user &&
			state.user?.isNovated &&
			(state.user?.income == null || state.user?.distanceDriven == null),
		accessToken: (state) =>
			state.user ? state.user.personalAccessToken : null,
		firstName: (state) => (state.user ? state.user.firstName : ''),
		lastName: (state) => (state.user ? state.user.lastName : ''),
		otpFlowOrDefault: (state) => state.otpFlow || 'login',
		getIsSigningOut: (state) => state.isSigningOut,
	},
	actions: {
		/**
		 * Step 1: Verify credentials and request OTP
		 * Backend handles OTP sending internally and returns an user_token
		 */
		async requestOtpLogin(email, password, recaptcha, options = {}) {
			// Call backend - it verifies credentials AND sends OTP
			const data = await useFetchForAuth('/secure/authenticate', {
				method: 'POST',
				body: { email, password, _recaptcha: recaptcha },
				...options,
			})

			// Direct login path (user not yet registered for OTP) — backend returns user object
			if (data.user) {
				this.setUser(data)

				return {
					success: data.success,
					message: data.message,
					user: this.user,
				}
			}

			// OTP path — backend returns user_token for the verify-otp step
			return {
				success: data.success,
				message: data.message,
				user_token: data.user_token,
			}
		},

		/**
		 * Step 2: Verify OTP and complete login
		 * Backend handles OTP verification and creates session
		 */
		async verifyOtp(otpCode, userToken, options = {}) {
			const data = await useFetchForAuth('/secure/verify-otp', {
				method: 'POST',
				body: { otp_code: otpCode, user_token: userToken },
				...options,
			})

			this.setUser(data)

			return { user: this.user }
		},

		async requestOtp(options = {}) {
			const data = await useFetchForAuth('/secure/request-otp', {
				method: 'POST',
				body: { user_token: this.userToken },
				...options,
			})

			if (data.user_token) {
				this.userToken = data.user_token
			}

			return {
				success: data.success,
				message: data.message,
			}
		},

		// Signs in from a login or verify-otp response
		setUser({ user, access_token }) {
			const runtimeConfig = useRuntimeConfig()

			this.user = {
				id: user.id,
				uuid: user.uuid,
				firstName: user.first_name,
				lastName: user.last_name,
				personalAccessToken: access_token,
				isNovated: user.is_novated,
				income: user.income,
				distanceDriven: user.distance_driven,
				cookieVersion: runtimeConfig.public.AUTH_COOKIE_VERSION,
			}
		},

		/**
		 * Whether the API session is still on. A 401 (e.g. 24 hours idle) signs out locally with a message for
		 * the sign-in page; other errors count as signed in. Browser only: the cookie is on the API's host.
		 */
		async verifySession() {
			try {
				await useFetchForAuth('/secure/session')
			} catch (error) {
				if (error?.status !== 401) {
					return true
				}

				this.setSessionError(
					'Your session has expired. Please sign in again.'
				)
				this.user = null

				return false
			}

			return true
		},

		// This signIn function is legacy and is used in
		// email-validation,auroraenergy and sonnendrive
		async signIn(
			email,
			password,
			recaptcha,
			enableRedirect = true,
			options = {}
		) {
			// we aren't running a try/catch here as we want to pass errors straight back to code calling this function
			const { user } = await this.requestOtpLogin(
				email,
				password,
				recaptcha,
				options
			)

			// Accounts that sign in with an emailed code can't finish here
			if (!user) {
				throw new Error('Please sign in with the code we emailed you.')
			}

			if (enableRedirect === false) {
				return
			}

			const { redirect } = this
			this.redirect = null

			const { partnerName } = useGetPartnerName()
			navigateToWithOverride(
				partnerName.value ? `/partners/${partnerName.value}` : '/',
				{ external: true },
				redirect
			)
		},
		async signOut() {
			// This state will reset when page reload if navigateTo "external: true"
			this.isSigningOut = true

			// Return user to home page
			const { partnerName } = useGetPartnerName()
			const targetUrl = partnerName.value
				? `/partners/${partnerName.value}`
				: '/'

			// Ends the API session and revokes this login's access token
			try {
				await useFetchForAuth('/secure/logout', { method: 'POST' })
			} catch (error) {
				console.error('[signOut] Logout API failed:', error)
			}

			// Keep state persisted in pinia but remove auth cookie.
			// To show profile icon UI until page reloads.
			const authCookie = useCookie('carbarAuAuth')
			authCookie.value = null

			await navigateTo(targetUrl)

			this.user = null
			this.isSigningOut = false
		},
		async resendVerificationEmail(email) {
			const { data } = await useFetchForApi(`/resend-validation-email`, {
				method: 'POST',
				body: { email },
			})

			return data.success
		},
		setOtpData(token, email, requestedAt = null) {
			this.userToken = token
			this.otpEmail = email
			this.otpRequestedAt = requestedAt
		},
		clearStaleOtpSession(currentFlow) {
			if (
				this.userToken &&
				this.otpEmail &&
				this.otpFlow !== currentFlow
			) {
				this.clearOtpData()
			}
		},
		clearOtpData() {
			this.userToken = null
			this.otpEmail = null
			this.otpRequestedAt = null
			this.otpFlow = null
			this.sessionError = null
			this.isSigningOut = false
		},
		setOtpFlow(flow) {
			this.otpFlow = flow
		},
		clearOtpFlow() {
			this.otpFlow = null
		},
		setSessionError(message) {
			this.sessionError = message
		},
		clearSessionError() {
			this.sessionError = null
		},
		storeRedirect(redirect) {
			this.redirect = redirect
		},
		removeRedirect() {
			this.redirect = null
		},
	},
	persist: {
		storage: piniaPluginPersistedstate.cookies({
			sameSite: 'strict',
			expires: expiryDate,
		}),
		pick: [
			'user',
			'redirect',
			'userToken',
			'otpEmail',
			'otpRequestedAt',
			'otpFlow',
			'sessionError',
		],
	},
})

if (import.meta.hot) {
	import.meta.hot.accept(
		acceptHMRUpdate(useCarbarAuAuthStore, import.meta.hot)
	)
}
